import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, ShieldCheck, ArrowLeft, Smartphone } from 'lucide-react';

const LoginPage = ({ onLogin }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP step state
  const [otpStep, setOtpStep] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpPhoneMasked, setOtpPhoneMasked] = useState('');
  const [otp, setOtp] = useState('');
  const [remaining, setRemaining] = useState(0);
  const [devMode, setDevMode] = useState(false);
  const tickRef = useRef(null);

  // Countdown for the 5-minute OTP TTL
  useEffect(() => {
    if (!otpStep) return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [otpStep]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const clean = (phone || '').replace(/\D/g, '').slice(-10);
    if (clean.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/staff/login/request-otp', { phone: clean });
      const { otp_token, otp_phone_masked, expires_in_seconds, dev_mode } = response.data || {};
      setOtpToken(otp_token);
      setOtpPhoneMasked(otp_phone_masked || '');
      setRemaining(expires_in_seconds || 300);
      setDevMode(!!dev_mode);
      setOtp('');
      setOtpStep(true);
      if (dev_mode) {
        toast.info('Dev preview: use OTP 123456');
      } else {
        toast.success(`OTP sent to ${otp_phone_masked || 'your registered phone'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error('Enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/staff/login/verify-otp', { otp_token: otpToken, otp });
      const { token, user } = response.data;
      toast.success('Login successful!');
      onLogin(token, user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
      if (error.response?.status === 401 && /session/i.test(error.response?.data?.detail || '')) {
        setOtpStep(false);
        setOtp('');
        setOtpToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setOtpStep(false);
    setOtp('');
    setOtpToken('');
    setRemaining(0);
    setDevMode(false);
  };

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
    }}>
      <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-white/90 p-4 rounded-2xl mb-4">
            {otpStep ? <ShieldCheck className="w-12 h-12 text-purple-600" /> : <Calendar className="w-12 h-12 text-purple-600" />}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{otpStep ? 'Verify OTP' : 'Clinic Manager'}</h1>
          <p className="text-white/80 text-sm text-center">
            {otpStep
              ? `One-time code sent to ${otpPhoneMasked}`
              : 'Sign in with your registered mobile number'}
          </p>
        </div>

        {!otpStep ? (
          <form onSubmit={handleRequestOtp} className="space-y-6">
            <div>
              <Label htmlFor="phone" className="text-white/90 mb-2 block">Mobile Number</Label>
              <div className="relative">
                <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400" />
                <span className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium select-none">+91</span>
                <Input
                  id="phone"
                  data-testid="login-phone-input"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="pl-20 bg-white/90 border-white/30 h-12 rounded-xl font-mono tracking-wider"
                  placeholder="10-digit mobile"
                  required
                  autoFocus
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-send-otp-button"
              disabled={loading || phone.length !== 10}
              className="w-full h-12 rounded-xl bg-white text-purple-600 hover:bg-white/90 font-semibold text-lg disabled:opacity-60"
            >
              {loading ? 'Sending OTP…' : 'Send OTP'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div>
              <Label htmlFor="otp" className="text-white/90 mb-2 block">6-digit OTP</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400" />
                <Input
                  id="otp"
                  data-testid="login-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="pl-10 bg-white/90 border-white/30 h-12 rounded-xl text-center tracking-[0.5em] text-lg font-mono"
                  placeholder="••••••"
                  required
                  autoFocus
                />
              </div>
              <p className="text-xs text-white/70 mt-2 text-center" data-testid="login-otp-timer">
                {remaining > 0
                  ? `Code expires in ${mins}:${secs.toString().padStart(2, '0')}`
                  : 'Code expired. Go back and try again.'}
              </p>
              {devMode && (
                <p className="text-[11px] text-yellow-100/90 mt-1 text-center" data-testid="login-dev-hint">
                  Dev preview — use <span className="font-mono font-bold">123456</span>
                </p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="login-verify-otp-button"
              disabled={loading || remaining === 0}
              className="w-full h-12 rounded-xl bg-white text-purple-600 hover:bg-white/90 font-semibold text-lg disabled:opacity-60"
            >
              {loading ? 'Verifying…' : 'Verify & Sign in'}
            </Button>

            <button
              type="button"
              onClick={handleBack}
              data-testid="login-back-button"
              className="w-full flex items-center justify-center gap-1 text-white/80 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
